import { Criterion, Form, ListValueType, Quota, Range } from '@app/api';

export const updateQuotasValues = (
  quotas: Quota[] | null,
  oldNumberOfParticipantRequired: number,
  newNumberOfParticipantRequired: number,
): Quota[] | null => {
  if (!quotas || quotas.length === 0) {
    return quotas;
  }

  const scaleFactor =
    newNumberOfParticipantRequired / oldNumberOfParticipantRequired;
  const adjustedQuotas = quotas.map((quota) => ({
    ...quota,
    value: quota.value * scaleFactor,
  }));

  const flooredQuotas = adjustedQuotas.map((quota) => ({
    ...quota,
    value: Math.floor(quota.value),
  }));

  const flooredTotal = flooredQuotas.reduce(
    (sum, quota) => sum + quota.value,
    0,
  );
  const difference = newNumberOfParticipantRequired - flooredTotal;

  const sortedByRemainder = adjustedQuotas.map((quota, index) => ({
    index,
    remainder: quota.value - Math.floor(quota.value),
  }));

  if (difference > 0) {
    sortedByRemainder
      .sort((a, b) => b.remainder - a.remainder)
      .slice(0, difference)
      .forEach(({ index }) => {
        flooredQuotas[index].value += 1;
      });
  } else if (difference < 0) {
    sortedByRemainder
      .sort((a, b) => a.remainder - b.remainder)
      .slice(0, Math.abs(difference))
      .forEach(({ index }) => {
        flooredQuotas[index].value -= 1;
      });
  }

  return flooredQuotas;
};

export function divideQuotas(
  totalPeople: number,
  criteriaCount: number,
): number[] {
  if (criteriaCount === 0) {
    return [];
  }
  const baseCount = Math.floor(totalPeople / criteriaCount);
  const remainder = totalPeople % criteriaCount;

  const result = Array(criteriaCount).fill(baseCount);
  for (let i = 0; i < remainder; i++) {
    result[i] += 1;
  }

  return result;
}

export const areQuotasValid = (criteria: Criterion[]): boolean => {
  for (const criterion of criteria) {
    if (!criterion.quotas) continue;
    if (
      criterion.listValueType === ListValueType.AGE_RANGE &&
      criterion.allowList?.length === 1
    ) {
      if (!checkCriterionRangeCoverage(criterion.quotas, criterion)) {
        return false;
      }
    }
  }
  return true;
};

export const isValidRange = (
  value: unknown,
): value is { min: number; max: number | null } => {
  return (
    Boolean(value) &&
    typeof (value as Range).min === 'number' &&
    (typeof (value as Range).max === 'number' || (value as Range).max === null)
  );
};

export const sortRangeQuotas = (quotas: Quota[]) => {
  return [...quotas].sort((a, b) => {
    const aMin = isValidRange(a.item) ? a.item.min ?? 0 : 0;
    const bMin = isValidRange(b.item) ? b.item.min ?? 0 : 0;
    return aMin - bMin;
  });
};

export const getRangeMax = (val: number | null | undefined) =>
  val === null || val === undefined ? Infinity : val;

export const checkCriterionRangeCoverage = (
  quotas: Quota[],
  criterion: Criterion,
): boolean => {
  const overallRange = criterion.allowList?.[0];
  if (!isValidRange(overallRange)) return true;

  const sortedQuotas = sortRangeQuotas(quotas);
  let currentMin = overallRange.min ?? 0;
  const overallMax = getRangeMax((overallRange as Range).max);

  let foundUnlimited = false;
  for (const quota of sortedQuotas) {
    if (!isValidRange(quota.item)) continue;
    const quotaMin = quota.item.min ?? 0;
    const quotaMax = getRangeMax(quota.item.max);

    // If quota max=null, it must be the only one and must be at the end of the sortedQuotas
    if (quota.item.max === null) {
      if (foundUnlimited) return false; //
      foundUnlimited = true;
      if (quotaMin !== currentMin) return false; // non-covered range before infinity
      currentMin = Infinity;
      continue;
    }

    if (quotaMin < (overallRange.min ?? 0) || quotaMax > overallMax) {
      return false;
    }
    if (quotaMin > currentMin) {
      return false; // Found a non-covered range
    }
    currentMin = Math.max(currentMin, quotaMax + 1);
  }

  // if overallMax is Infinity, there must be a quota with max=null
  if (overallMax === Infinity) {
    return foundUnlimited;
  }

  return currentMin > overallMax;
};

export const matchCriteriaWithForm = (
  form: Form | undefined,
  criteria: Criterion[] | undefined,
) => {
  if (!form || !form.fields) {
    return criteria ?? [];
  }

  if (!criteria || !criteria.length) {
    return [];
  }

  for (const field of form.fields) {
    if (field.type !== 'single_choice') {
      continue;
    }

    const criterion = criteria?.find((c) => c.fieldRef === field.ref);
    if (!criterion) {
      continue;
    }

    const cIndex = criteria?.findIndex((c) => c.fieldRef === field.ref);
    const choices =
      field.properties?.choices?.map((choice) => choice.ref) ?? [];
    for (const cri of [...(criterion.allowList ?? []), ...criterion.denyList]) {
      if (!choices.includes(cri.toString())) {
        criteria[cIndex] = {
          ...criterion,
          allowList: criterion.allowList?.filter((c) => c !== cri),
        };
        criteria[cIndex] = {
          ...criteria[cIndex],
          quotas: criterion.quotas?.filter((c) => c.item !== cri),
        };
        criteria[cIndex] = {
          ...criteria[cIndex],
          denyList: criterion.denyList.filter((c) => c !== cri),
        };
      }
    }
  }

  return criteria;
};
